import { CubeIcon } from '@heroicons/react/24/outline';
import { useSlideOver } from '../../legacy-components/slide-over/slide-over-context';
import { ArrowRightInCircleIcon } from '../icons/ArrowRightInCircleIcon';
import { encodePrn, useGetAsset } from '../../utils/get-asset';

import { useTranslation } from 'react-i18next';

export type AssetNameProps = {
  /** `asset.id` in PRN format i.e. prn:assets:{integrationId}:aws:ec2:instance:{region}:i-021234567890 */
  id: string;
  /** `asset.name` which is human-readable */
  name?: string | null;
  /** Lets a long name use the width it's given, where the default stops at 320px. */
  fullWidth?: boolean;
  /**
   * Shows `name` as given, without fetching the asset for its name. For lists,
   * where a fetch per row would cost a request each.
   */
  isNameFinal?: boolean;
};

/**
 * AssetName atom which opens Asset details slide-over on click and can display a human readable `name`
 *
 * Example:
 * ```tsx
 *   <AssetName id={asset.id} name={asset.name} />
 * ```
 */
export const AssetName = ({
  id: prn,
  name,
  fullWidth = false,
  isNameFinal = false,
}: AssetNameProps) => {
  const { t } = useTranslation('designSystem');
  const { contextualView } = useSlideOver();
  const { data } = useGetAsset(isNameFinal && name ? undefined : prn);
  const assetName = data?.asset.name || prn;

  // In dark mode the chip is a shade darker than whatever it sits on: the
  // neutral background is the cards' own grey there, which would hide it.
  return (
    <span
      aria-label={t('asset')}
      data-testid="asset-name"
      className={`bg-neutral-background text-link-base hover:text-link-hover py-xs rounded-badge relative inline-flex dark:bg-black/30 ${fullWidth ? 'max-w-full' : 'max-w-80'} cursor-pointer flex-row items-center gap-y-2 px-2 text-sm`}
      onClick={() =>
        contextualView({
          type: 'assetDetail',
          slideOverClassName: 'max-w-[70vw] bg-white dark:bg-gray-900',
          title: t('assetDetails'),
          data: { prn, manualNavigation: true },
          overlay: true,
          expandLink: `/assets/${encodePrn(prn)}`,
        })
      }
    >
      <CubeIcon className="mr-1 inline-block h-3.5 w-3.5 shrink-0" />
      <span className="mr-2 truncate underline underline-offset-2">
        {name || assetName}
      </span>
      <ArrowRightInCircleIcon className="h-3.5 w-3.5 shrink-0" />
    </span>
  );
};
